import * as yup from 'yup';
import { ArrowDown, ArrowUp, Eye, EyeOff, Trash2 } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';
import sum from 'lodash/sum';

import { GetProductQuery as GetProduct } from '__generated__/graphql';

import { ErrorBag, getServerErrors } from '../../../../common/helpers';
import { TranslatableInput } from '../../../Translations/Inputs';
import { translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import useEditProductMutation from './useEditProductMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Product = GetProduct['product'];
type ProductVariant = Product['product_variants'][0];
type Event = GetProduct['event'];

const editProductVariantsForm = yup.object().shape({
  product_variants: yup.array(yup.object().shape({
    title: yup.string().required().max(50),
    capacity: yup.number().nullable(),
  })),
});

export interface EditProductVariantsFormProps {
  product: Product;
  event: Event
}

interface ProductVariantFormProps {
  form: FormManager;
  index: number;
  product: Product;
  productVariant?: ProductVariant;
  errors: ErrorBag;
  moveProductVariantUp: () => void;
  moveProductVariantDown: () => void;
  toggleProductVariant: () => void;
  deleteProductVariant: () => void;
}

const MobileProductVariantForm = ({
  form,
  product,
  productVariant,
  index,
  errors,
  moveProductVariantUp,
  moveProductVariantDown,
  toggleProductVariant,
  deleteProductVariant,
}: ProductVariantFormProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const integerInput = useIntegerInput();

  const key = (k?: string) => `product_variants.${index}${k ? `.${k}` : ''}`;

  return (
    <UI.Div sc={{ background: 'gray.100', padding: 4 }}>
      <UI.FormGrid sc={{ column: 'span 2' }}>
        <UI.InputGroup sc={{ valid: !errors[key('title')], touched: form.getTouched(key('title')) }}>
          <UI.InputLabel>
            {t('product_variant_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <TranslatableInput
            form={form}
            name={key('title')}
            defaultLocale={product.locale}
            title={t('product_variant_title')}
            maxLength={50}
            placeholder={t('product_variant_title_placeholder')}
          />
          <UI.ErrorMessages
            attribute={t('product_variant_title')}
            errors={errors[key('title')]}
          />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{
            valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')),
          }}
        >
          <UI.InputLabel>{t('capacity')}</UI.InputLabel>

          <UI.DebouncedInput
            {...form.text(key('capacity'), { inputHandler: integerInput() })}
            format={integerInput().format}
            maxLength={10}
            inputMode="numeric"
            match={/[\d]/g}
            placeholder="∞"
          />
          <UI.ErrorMessages
            attribute={t('capacity')}
            errors={errors[key('capacity')]}
          />
        </UI.InputGroup>

        <UI.FormGrid sc={{
          columns: '1fr fit-content(136px)',
          gutter: 0.25,
        }}
        >
          <UI.Div sc={{ pt: 1, small: true }}>
            {`${t('purchases')}: `}
            {productVariant ? formatInteger(productVariant.sold_count) : 0}
          </UI.Div>

          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.Button
              onClick={() => moveProductVariantUp()}
              sc={{ blank: true, size: 'sm', color: 'gray.400' }}
              disabled={index === 0}
            >
              <UI.Icon>
                <ArrowUp />
              </UI.Icon>
            </UI.Button>
            <UI.Button
              onClick={() => moveProductVariantDown()}
              sc={{ blank: true, size: 'sm', color: 'gray.400' }}
              disabled={index === form.values.product_variants.length - 1}
            >
              <UI.Icon>
                <ArrowDown />
              </UI.Icon>
            </UI.Button>
            <UI.Button
              onClick={() => toggleProductVariant()}
              sc={{ blank: true, size: 'sm', color: 'gray.400' }}
              aria-label={form.get(key('active')) ? t('disable') : t('enable')}
            >
              <UI.Icon>
                {form.get(key('active')) ? <Eye /> : <EyeOff />}
              </UI.Icon>
            </UI.Button>
            <UI.Button
              onClick={() => deleteProductVariant()}
              sc={{ blank: true, color: 'gray.400', size: 'sm' }}
              disabled={productVariant && !productVariant.deletable}
            >
              <UI.Icon>
                <Trash2 />
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>
      </UI.FormGrid>
    </UI.Div>
  );
};

const DesktopProductVariantForm = ({
  form,
  index,
  errors,
  product,
  productVariant,
  moveProductVariantUp,
  moveProductVariantDown,
  toggleProductVariant,
  deleteProductVariant,
}: ProductVariantFormProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const integerInput = useIntegerInput();

  const key = (k?: string) => `product_variants.${index}${k ? `.${k}` : ''}`;

  return (
    <UI.FormGrid sc={{
      columns: '2fr 1fr 1fr 136px',
      gutter: 0.5,
    }}
    >
      <UI.InputGroup sc={{ valid: !errors[key('title')], touched: form.getTouched(key('title')) }}>
        <TranslatableInput
          form={form}
          name={key('title')}
          defaultLocale={product.locale}
          title={t('product_variant_title')}
          maxLength={50}
          placeholder={t('product_variant_title_placeholder')}
        />
        <UI.ErrorMessages
          attribute={t('product_variant_title')}
          errors={errors[key('title')]}
        />
      </UI.InputGroup>

      <UI.InputGroup
        sc={{
          valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')),
        }}
      >
        <UI.DebouncedInput
          {...form.text(key('capacity'), { inputHandler: integerInput() })}
          format={integerInput().format}
          maxLength={10}
          inputMode="numeric"
          match={/[\d]/g}
          placeholder="∞"
        />
        <UI.ErrorMessages
          attribute={t('capacity')}
          errors={errors[key('capacity')]}
        />
      </UI.InputGroup>

      <UI.Div sc={{ pt: 1.5 }}>
        {productVariant ? formatInteger(productVariant.sold_count) : 0}
      </UI.Div>

      <UI.Div sc={{ pt: 0.75, textAlign: 'right' }}>
        <UI.Button
          onClick={() => moveProductVariantUp()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          disabled={index === 0}
        >
          <UI.Icon>
            <ArrowUp />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => moveProductVariantDown()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          disabled={index === form.values.product_variants.length - 1}
        >
          <UI.Icon>
            <ArrowDown />
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => toggleProductVariant()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          aria-label={form.get(key('active')) ? t('disable') : t('enable')}
        >
          <UI.Icon>
            {form.get(key('active')) ? <Eye /> : <EyeOff />}
          </UI.Icon>
        </UI.Button>
        <UI.Button
          onClick={() => deleteProductVariant()}
          sc={{ blank: true, size: 'sm', color: 'gray.400' }}
          disabled={productVariant && !productVariant.deletable}
        >
          <UI.Icon>
            <Trash2 />
          </UI.Icon>
        </UI.Button>
      </UI.Div>
    </UI.FormGrid>
  );
};

const EditProductVariantsForm = ({ product, event }: EditProductVariantsFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  const getValues = (product: Product) => ({
    product_variants: product.product_variants.map((productVariant) => ({
      id: productVariant.id,
      title: productVariant.title,
      title_translations: translationsForAttribute(productVariant.translations, 'title'),
      capacity: productVariant.capacity,
      active: productVariant.active,
    })),
  });

  const form = useForm({
    values: getValues(product),
  });

  const device = useMediaDevice();

  useNavigationPrompt(form.changed());

  const [editProduct, { error, loading }] = useEditProductMutation({
    input: {
      id: product.id,
      product_variants: form.values.product_variants,
    },
    onSuccess: (product) => {
      form.reset(getValues(product));
    },
  });

  const { valid, errors } = useValidation({
    schema: editProductVariantsForm,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const productVariantKeys = useRef(form.values.product_variants.map((value, index) => index));

  const addProductVariant = () => {
    form.append('product_variants', {
      title_translations: [],
      active: true,
    });
    productVariantKeys.current.push(Math.max(0, ...productVariantKeys.current) + 1);
  };

  const moveProductVariantUp = (index: number) => {
    form.moveUp('product_variants', index);

    const key = productVariantKeys.current[index];
    productVariantKeys.current[index] = productVariantKeys.current[index - 1];
    productVariantKeys.current[index - 1] = key;
  };

  const moveProductVariantDown = (index: number) => {
    form.moveDown('product_variants', index);

    const key = productVariantKeys.current[index];
    productVariantKeys.current[index] = productVariantKeys.current[index + 1];
    productVariantKeys.current[index + 1] = key;
  };

  const toggleProductVariant = (index: number) => {
    form.set(`product_variants.${index}.active`, (value: boolean) => !value);
  };

  const deleteProductVariant = (index: number) => {
    form.delete('product_variants', index);
    productVariantKeys.current.splice(index, 1);
  };

  const productVariants = keyBy(product.product_variants, 'id');
  const unchosenVariantsCount = product.sold_count - sum(product.product_variants.map((variant) => variant.sold_count));
  const ProductVariantForm = device.isSmall ? MobileProductVariantForm : DesktopProductVariantForm;

  return (
    <UI.Form onSubmit={() => editProduct()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('product_variants')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_variants_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ gutter: 0.5 }}>
          {!device.isSmall && form.values.product_variants.length > 0 && (
            <UI.FormGrid sc={{
              columns: '2fr 1fr 1fr 136px',
              gutter: 0.5,
            }}
            >
              <UI.InputLabel sc={{ my: 0 }}>
                {t('product_variant_title')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>

              <UI.InputLabel sc={{ my: 0 }}>{t('capacity')}</UI.InputLabel>

              <UI.InputLabel sc={{ my: 0 }}>{t('purchases')}</UI.InputLabel>
            </UI.FormGrid>
          )}
          {form.values.product_variants.map((productVariant: any, index: number) => (
            <ProductVariantForm
              form={form}
              index={index}
              product={product}
              productVariant={productVariants[productVariant.id]}
              errors={errors}
              moveProductVariantUp={() => moveProductVariantUp(index)}
              moveProductVariantDown={() => moveProductVariantDown(index)}
              toggleProductVariant={() => toggleProductVariant(index)}
              deleteProductVariant={() => deleteProductVariant(index)}
              key={productVariantKeys.current[index]}
            />
          ))}
          <UI.Div>
            <UI.Button onClick={() => addProductVariant()}>
              {t('add_variant')}
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>

        {unchosenVariantsCount > 0 && (
          <UI.Info>
            {t('product_variant_unchosen_description', {
              count: unchosenVariantsCount, value: formatNumber(unchosenVariantsCount),
            })}
          </UI.Info>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_product')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditProductVariantsForm;
